import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChefHat, Factory, Lightbulb, TrendingUp, Clock, Phone, Mail, MapPin, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { mockServices, mockStats, mockClients } from '../utils/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);

      if (response.data.success) {
        toast.success(response.data.message);
        setFormData({ name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      
      if (error.response && error.response.data && error.response.data.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Failed to send message. Please try again or contact us directly.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const serviceIcons = {
    'Food Manufacturing Plant Setup': <Factory className="w-12 h-12" />,
    'New Recipe Development': <ChefHat className="w-12 h-12" />,
    'Recipe Enhancement': <Lightbulb className="w-12 h-12" />,
    'Shelf Life Solutions': <Clock className="w-12 h-12" />
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <ChefHat className="w-8 h-8 text-orange-600" />
              <span className="text-2xl font-bold text-gray-900">BSFOODCONSULTANT</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="nav-link">Home</Link>
              <Link to="/about" className="nav-link">About Us</Link>
              <Link to="/services" className="nav-link">Services</Link>
              <Link to="/contact" className="nav-link">Contact Us</Link>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-gray-700 hover:text-orange-600 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden pt-4 pb-2 flex flex-col space-y-3">
              <Link to="/" className="nav-link-mobile">Home</Link>
              <Link to="/about" className="nav-link-mobile">About Us</Link>
              <Link to="/services" className="nav-link-mobile">Services</Link>
              <Link to="/contact" className="nav-link-mobile">Contact Us</Link>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-yellow-50 to-green-50 opacity-70"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="hero-title">
                Food Consultant<br />
                <span className="text-orange-600">& Recipe Maker</span>
              </h1>
              <p className="text-xl text-gray-700 leading-relaxed">
                Transforming culinary visions into reality with over 12 years of expertise in food manufacturing, recipe development, and quality enhancement.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg">
                  Get Started
                </Button>
                <Button variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-50 px-8 py-6 text-lg">
                  Learn More
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.unsplash.com/photo-1581600140682-d4e68c8cde32?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxzcGljZXN8ZW58MHx8fHwxNzY5MTUzMTA2fDA&ixlib=rb-4.1.0&q=85"
                alt="Colorful spices"
                className="rounded-2xl shadow-xl object-cover h-64 w-full hover:scale-105 transition-transform duration-300"
              />
              <img
                src="https://images.unsplash.com/photo-1506368249639-73a05d6f6488?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxzcGljZXN8ZW58MHx8fHwxNzY5MTUzMTA2fDA&ixlib=rb-4.1.0&q=85"
                alt="Spice powders"
                className="rounded-2xl shadow-xl object-cover h-64 w-full hover:scale-105 transition-transform duration-300 mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="section-title">What We Do</h2>
            <p className="text-xl text-gray-600 mt-4">Our Services</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockServices.map((service, index) => (
              <Card key={index} className="service-card group">
                <CardContent className="p-8 text-center space-y-4">
                  <div className="text-orange-600 group-hover:text-orange-700 transition-colors duration-300 flex justify-center">
                    {serviceIcons[service.title]}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Client Logos Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="section-title">Trusted By Industry Leaders</h2>
            <p className="text-xl text-gray-600 mt-4">12 Years of Excellence</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-8">
            {mockClients.map((client, index) => (
              <div key={index} data-testid={`client-logo-${index}`} className="client-logo-card">
                <div className="flex items-center justify-center h-32 bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-4 hover:scale-105">
                  {client.image ? (
                    <img
                      src={client.image}
                      alt={client.name}
                      className="max-h-full max-w-full object-contain rounded-lg"
                    />
                  ) : (
                    <span className="text-gray-400 font-semibold text-sm text-center">{client.name}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-orange-600 text-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {mockStats.map((stat, index) => (
              <div key={index} className="text-center space-y-2 stat-card">
                <div className="text-5xl md:text-6xl font-bold">{stat.value}</div>
                <div className="text-lg md:text-xl opacity-90">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white" id="contact">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="section-title">Get In Touch</h2>
            <p className="text-xl text-gray-600 mt-4">Let's discuss your food consulting needs</p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <Card className="shadow-xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your Name"
                      required
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      required
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+1 234 567 8900"
                      required
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Message</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us about your project..."
                      rows={4}
                      required
                      className="w-full"
                    />
                  </div>
                  <Button type="submit" disabled={isSubmitting} className="w-full bg-orange-600 hover:bg-orange-700 text-white py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed">
                    <Send className="w-5 h-5 mr-2" />
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Mail className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900">Email</h3>
                  <p className="text-gray-600">BSFoodconsultants@gmail.com</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Phone className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900">Phone</h3>
                  <p className="text-gray-600">+91 9456808695</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <MapPin className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900">Address</h3>
                  <p className="text-gray-600">Plot No: 332 - Pandev Nager Indl. Area</p>
                  <p className="text-gray-600">Ghaziabad 201001 (UP)</p>
                  <p className="text-gray-600">India</p>
                </div>
              </div>

              <Card className="bg-gradient-to-br from-orange-500 to-yellow-500 text-white shadow-xl">
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold mb-2">Ready to Start?</h3>
                  <p className="mb-4">Schedule a free consultation with our experts today!</p>
                  <Button variant="secondary" className="w-full bg-white text-orange-600 hover:bg-gray-100">
                    Book Consultation
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <ChefHat className="w-6 h-6 text-orange-500" />
                <span className="text-xl font-bold">BSFOODCONSULTANT</span>
              </div>
              <p className="text-gray-400">Your trusted partner in food manufacturing excellence and recipe innovation.</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-orange-500 transition-colors">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-orange-500 transition-colors">About Us</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-orange-500 transition-colors">Services</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Services</h4>
              <ul className="space-y-2">
                <li className="text-gray-400">Plant Setup</li>
                <li className="text-gray-400">Recipe Development</li>
                <li className="text-gray-400">Recipe Enhancement</li>
                <li className="text-gray-400">Shelf Life Solutions</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-2">
                <li className="text-gray-400">BSFoodconsultants@gmail.com</li>
                <li className="text-gray-400">+91 9456808695</li>
                <li className="text-gray-400">Plot No: 332 - Pandev Nager</li>
                <li className="text-gray-400">Ghaziabad 201001 (UP) India</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2025 BSFOODCONSULTANT. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;